import '@vaadin/text-field';
// eslint-disable-next-line no-duplicate-imports
import type { TextFieldValueChangedEvent, TextField as VaadinTextField } from '@vaadin/text-field';
import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { createRef, ref, Ref } from 'lit/directives/ref.js';
import { circleCloseFilledIcon } from '../assets/icons';
import { TmInputFieldBase } from './tm-input-field.base';
export const tagName = 'tm-text-field';

@customElement(tagName)
export class TmTextField extends TmInputFieldBase<string> {
    @property({ type: String })
    public pattern?: string;

    @property({ type: String })
    public allowedCharacters?: string;

    @property({ type: Number })
    public minLength?: number;

    @property({ type: Number })
    public maxLength?: number;

    @property({ type: Boolean })
    public clearButton = false;

    @property({ type: Boolean, reflect: true })
    public large = false;

    @property({ type: Boolean, reflect: true })
    public alignHelperTextRight = false;

    @property({ type: Boolean, reflect: true })
    public invalid = false;

    private readonly inputRef = createRef<HTMLInputElement>();

    private get vaadinTextField() {
        return (this.inputFieldRef as Ref<VaadinTextField>).value;
    }

    private get previousValue() {
        return this._previousValue;
    }

    private set previousValue(value: string) {
        this._previousValue = value;
    }

    private _previousValue: string = '';

    public connectedCallback() {
        super.connectedCallback();
        this.listenForValueChange(({ value }) => {
            if (!this.inputField || this.inputField.value === value) {
                return;
            }
            // we have to set this explicitly since Vaadin controls don't use reactive props
            this.inputField.value = value || '';
        });
    }

    protected validate(): boolean | string[] {
        if (!this.value && this.required) {
            return false;
        }

        return this.vaadinTextField ? this.vaadinTextField.validate() : true;
    }

    protected render() {
        const errorMessage = this.errorMessages?.join('\r\n');
        return html`
            <style>
                vaadin-text-field::part(clear-button)::before {
                    content: url(${`'data:image/svg+xml,${encodeURIComponent(
                        circleCloseFilledIcon.strings[0]
                    )}'`});
                }
            </style>
            <vaadin-text-field
                ${ref(this.inputFieldRef)}
                @value-changed=${(e: TextFieldValueChangedEvent) => {
                    const { value } = e.detail;
                    // backup for maxlength since Android allows text beyond maxlength to be entered
                    if (this.maxLength) {
                        if (value.length <= this.maxLength) {
                            this.changeValue(value.length > 0 ? value : undefined);
                            this.previousValue = value;
                        } else if (this.inputField) {
                            this.inputField.value = this.previousValue;
                        }
                    } else {
                        this.changeValue(value.length > 0 ? value : undefined);
                    }
                }}
                part="component"
                class=${classMap({
                    'w-full': true,
                    'align-right': this.alignHelperTextRight,
                    invalid: this.invalid,
                })}
                .label=${this.label}
                manual-validation
                ?large=${this.large}
                ?invalid="${!this.isValid}"
                error-message=${ifDefined(errorMessage)}
                value=${this.initial || ''}
                placeholder=${ifDefined(this.placeholder)}
                pattern=${ifDefined(this.pattern)}
                allowed-char-pattern=${ifDefined(this.allowedCharacters)}
                autocapitalize=${ifDefined(this.autocapitalize)}
                helper-text=${ifDefined(this.helperText)}
                .minlength=${this.minLength}
                .maxlength=${this.maxLength}
                ?required=${this.required}
                ?readonly=${this.readonly}
                ?disabled=${this.disabled}
                .invalid=${this.invalid}
                ?autofocus=${this.autofocus}
                ?clear-button-visible=${this.clearButton}
            >
                <slot slot="prefix" name="prefix"></slot>
                <input ${ref(this.inputRef)} slot="input" class="h-10 my-px ml-1" />
                <slot slot="suffix" name="suffix"></slot>
            </vaadin-text-field>
        `;
    }

    public static get styles() {
        return [
            ...super.styles,
            css`
                :host(:not([noOuterSpacing]))::part(component) {
                    padding-top: 8px;
                }

                :host([large]) {
                    --lumo-size-m: 3rem;
                }

                :host([autofocus]) {
                    --lumo-primary-color-50pct: none;
                }
                vaadin-text-field::part(helper-text) {
                    color: var(--secondary-text-color);
                    text-align: right;
                }
                vaadin-text-field::part(input-field) {
                    border: none;
                    border-bottom: 1px solid var(--input-field-border);
                    background-color: var(--input-field-background);
                    border-radius: 4px 4px 0 0;
                    color: var(--input-field-text);
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmTextField;
    }
}
